import type { ChartData } from '@/types/chart';

export interface ValidationResult {
  success: boolean;
  data?: ChartData;
  errors?: string[];
}

// Current supported schema version
const CURRENT_VERSION = 1;

/**
 * Validate chart data in grid format
 */
export function validateChartData(input: unknown): ValidationResult {
  const errors: string[] = [];

  // Basic type check
  if (typeof input !== 'object' || input === null) {
    return { success: false, errors: ['Input must be an object'] };
  }

  const data = input as Record<string, unknown>;

  // Check version field
  if (data.version === undefined) {
    errors.push('Missing "version" field (required, should be 1)');
  } else if (typeof data.version !== 'number' || data.version !== CURRENT_VERSION) {
    errors.push(`Unsupported version: ${data.version}. Expected version ${CURRENT_VERSION}`);
  }

  // Check required fields
  if (typeof data.name !== 'string' || data.name.trim() === '') {
    errors.push('Missing or invalid "name" field');
  }

  // Check hands field
  if (!Array.isArray(data.hands)) {
    return {
      success: false,
      errors: ['Missing or invalid "hands" field (must be a 2D array)'],
    };
  }

  const grid = data.hands as unknown[][];

  // Check grid dimensions
  if (grid.length !== 13) {
    errors.push(`Hands grid must have exactly 13 rows, got ${grid.length}`);
  }

  // Validate each row
  grid.forEach((row, rowIndex) => {
    if (!Array.isArray(row)) {
      errors.push(`Row ${rowIndex}: must be an array`);
      return;
    }

    if (row.length !== 13) {
      errors.push(`Row ${rowIndex}: must have exactly 13 columns, got ${row.length}`);
      return;
    }

    // Validate each cell
    row.forEach((cell, colIndex) => {
      if (cell === null) {
        // null is valid
        return;
      }

      if (typeof cell === 'number') {
        // Single number value is valid
        return;
      }

      if (typeof cell === 'string') {
        // String (category label) is valid
        return;
      }

      if (Array.isArray(cell)) {
        // Multi-segment array
        cell.forEach((seg, segIndex) => {
          if (typeof seg === 'number') {
            // number in array is valid
            return;
          }

          if (typeof seg === 'object' && seg !== null) {
            const s = seg as Record<string, unknown>;
            if (typeof s.value !== 'number') {
              errors.push(
                `Row ${rowIndex}, col ${colIndex}, segment ${segIndex}: segment object must have numeric "value" field`,
              );
            }
            // label is optional
          } else {
            errors.push(`Row ${rowIndex}, col ${colIndex}, segment ${segIndex}: segment must be number or object`);
          }
        });
      } else {
        errors.push(`Row ${rowIndex}, col ${colIndex}: cell must be null, number, string, or array`);
      }
    });
  });

  // Validate optional fields
  if (data.unit !== undefined && typeof data.unit !== 'string') {
    errors.push('"unit" field must be a string');
  }

  if (data.valueRange !== undefined) {
    if (typeof data.valueRange !== 'object' || data.valueRange === null) {
      errors.push('"valueRange" field must be an object');
    } else {
      const vr = data.valueRange as Record<string, unknown>;
      if (typeof vr.min !== 'number') {
        errors.push('"valueRange.min" must be a number');
      }
      if (typeof vr.max !== 'number') {
        errors.push('"valueRange.max" must be a number');
      }
    }
  }

  if (data.categoryMapping !== undefined) {
    if (typeof data.categoryMapping !== 'object' || data.categoryMapping === null) {
      errors.push('"categoryMapping" field must be an object');
    } else {
      const cm = data.categoryMapping as Record<string, unknown>;
      if (!Array.isArray(cm.categories)) {
        errors.push('"categoryMapping.categories" must be an array');
      }
      if (cm.direction !== undefined && cm.direction !== 'ascending' && cm.direction !== 'descending') {
        errors.push('"categoryMapping.direction" must be "ascending" or "descending"');
      }
    }
  }

  // Validate defaults field
  if (data.defaults !== undefined) {
    if (typeof data.defaults !== 'object' || data.defaults === null) {
      errors.push('"defaults" field must be an object');
    } else {
      const defaults = data.defaults as Record<string, unknown>;

      if (defaults.maxHeight !== undefined) {
        if (typeof defaults.maxHeight !== 'number') {
          errors.push('"defaults.maxHeight" must be a number');
        } else if (defaults.maxHeight < 0.5 || defaults.maxHeight > 4.0) {
          errors.push('"defaults.maxHeight" must be between 0.5 and 4.0');
        }
      }

      if (defaults.showLegend !== undefined && typeof defaults.showLegend !== 'boolean') {
        errors.push('"defaults.showLegend" must be a boolean');
      }

      if (defaults.showValues !== undefined && typeof defaults.showValues !== 'boolean') {
        errors.push('"defaults.showValues" must be a boolean');
      }

      if (defaults.useLogScale !== undefined && typeof defaults.useLogScale !== 'boolean') {
        errors.push('"defaults.useLogScale" must be a boolean');
      }

      if (defaults.segmentLayout !== undefined) {
        if (defaults.segmentLayout !== 'bookcase' && defaults.segmentLayout !== 'stacked') {
          errors.push('"defaults.segmentLayout" must be "bookcase" or "stacked"');
        }
      }

      if (defaults.segmentGap !== undefined) {
        if (typeof defaults.segmentGap !== 'number') {
          errors.push('"defaults.segmentGap" must be a number');
        } else if (defaults.segmentGap < 0 || defaults.segmentGap > 0.1) {
          errors.push('"defaults.segmentGap" must be between 0 and 0.1');
        }
      }

      if (defaults.colorScheme !== undefined && typeof defaults.colorScheme !== 'string') {
        errors.push('"defaults.colorScheme" must be a string');
      }
    }
  }

  if (errors.length > 0) {
    return { success: false, errors };
  }

  // Data is valid, return as ChartData
  return { success: true, data: data as unknown as ChartData };
}
